<script setup lang="ts">
/**
 * Saying whether you are coming (SPEC §6.12, §7.5, §8.6, §9.5).
 *
 * The screen the product is judged on. It is opened from a text message,
 * outdoors, one-handed, by somebody who has thirty seconds — so the common
 * answer is one tap and everything else is behind it.
 *
 * The tap is optimistic: the number changes immediately and rolls back if the
 * save fails. Waiting on a round trip over a car park's signal is how a
 * fifteen-second interaction becomes a minute of wondering whether it worked.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { ApiError, endpoints, type FamilyRsvpResponse } from '@/api'
import { useAppStore } from '@/stores/app'
import { asCount } from '@/utils/numbers'

const props = defineProps<{
  eventId: string
  rsvp: FamilyRsvpResponse
  defaultTotal: number
  /** False once replies have closed and this group does not take late ones. */
  open: boolean
}>()

const emit = defineEmits<{ saved: [FamilyRsvpResponse] }>()

const current = ref<FamilyRsvpResponse>(props.rsvp)
const saving = ref(false)
const error = ref<string | null>(null)

/** What just happened, for anybody not watching the numbers. */
const announcement = ref('')

const editing = ref(false)

/*
  While the counts are open, the "new version" prompt waits (SPEC §13.1).

  It cannot reload on its own, so nothing is lost if it does appear — but a
  banner sliding in under somebody's thumb while they choose a number is a
  banner that gets tapped. Keyed by event, so two cards on one screen do not
  release each other's hold.
*/
const app = useAppStore()
const holdKey = `rsvp:${props.eventId}`

watch([editing, saving], ([isEditing, isSaving]) => {
  if (isEditing || isSaving) app.holdUpdatePrompt(holdKey)
  else app.releaseUpdatePrompt(holdKey)
})

onBeforeUnmount(() => app.releaseUpdatePrompt(holdKey))

const adults = ref(asCount(props.rsvp.adultCount))
const children = ref(asCount(props.rsvp.childCount))
const note = ref(props.rsvp.note ?? '')

const answered = computed(() => current.value.status !== 'Unanswered')

const total = computed(() => props.defaultTotal)

const summary = computed(() => {
  const r = current.value

  if (r.status === 'Yes') {
    return asCount(r.attendingCount) === 1
      ? 'You said one of you is coming.'
      : `You said ${asCount(r.attendingCount)} of you are coming.`
  }

  if (r.status === 'No') return 'You said you cannot make it.'
  if (r.status === 'Maybe') return 'You said maybe.'

  return ''
})

function describe(caught: unknown): string {
  return (
    (caught instanceof ApiError && caught.problem?.title) ||
    'That did not save. Try again in a moment.'
  )
}

/**
 * Sends an answer, showing it first.
 *
 * The previous reply is kept so a failure puts the screen back exactly as it
 * was. Leaving an optimistic number on screen after a failed save is worse
 * than never having shown it.
 */
async function respond(
  status: 'Yes' | 'No' | 'Maybe',
  counts?: { adults: number; children: number },
) {
  const previous = current.value

  current.value = {
    ...previous,
    status,
    adultCount: counts?.adults ?? previous.adultCount,
    childCount: counts?.children ?? previous.childCount,
    attendingCount:
      status === 'Yes'
        ? (counts?.adults ?? asCount(previous.adultCount)) +
          (counts?.children ?? asCount(previous.childCount))
        : 0,
    note: note.value || null,
  }

  saving.value = true
  error.value = null

  try {
    const saved = await endpoints.updateFamilyRsvp(props.eventId, {
      status,
      adultCount: counts?.adults ?? null,
      childCount: counts?.children ?? null,
      note: note.value || null,
    })

    current.value = saved
    editing.value = false
    announcement.value = summary.value
    emit('saved', saved)
  } catch (caught) {
    current.value = previous
    error.value = describe(caught)
    announcement.value = 'That did not save.'
  } finally {
    saving.value = false
  }
}

function openDetail() {
  adults.value = asCount(current.value.adultCount)
  children.value = asCount(current.value.childCount)
  note.value = current.value.note ?? ''
  editing.value = true
}

function saveDetail(status: 'Yes' | 'No' | 'Maybe') {
  void respond(status, { adults: adults.value, children: children.value })
}

function step(which: 'adults' | 'children', by: number) {
  const target = which === 'adults' ? adults : children

  target.value = Math.max(0, target.value + by)
}
</script>

<template>
  <section class="rsvp">
    <p aria-live="polite" class="rsvp__announcement">{{ announcement }}</p>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>

    <p v-if="!open" class="rsvp__closed">
      Replies have closed for this one. Let whoever organizes your meals know and they can change
      it.
    </p>

    <template v-else-if="!editing">
      <p v-if="answered" class="rsvp__summary">{{ summary }}</p>

      <!--
        One tap for the common answer. Everything else is behind it, because
        the thing this screen is judged on is whether a returning household can
        finish in a single press (SPEC Principle 5).
      -->
      <v-btn
        v-if="!answered || current.status !== 'Yes'"
        :loading="saving"
        :disabled="saving"
        class="rsvp__primary"
        color="primary"
        variant="flat"
        size="large"
        block
        @click="respond('Yes')"
      >
        All {{ total }} of us are coming
      </v-btn>

      <div class="rsvp__secondary">
        <v-btn
          :disabled="saving"
          class="rsvp__action"
          variant="outlined"
          size="large"
          @click="openDetail"
        >
          Change
        </v-btn>
        <v-btn
          v-if="current.status !== 'No'"
          :loading="saving"
          :disabled="saving"
          class="rsvp__action"
          variant="outlined"
          size="large"
          @click="respond('No')"
        >
          Can't make it
        </v-btn>
      </div>
    </template>

    <div v-else class="rsvp__detail">
      <div class="rsvp__stepper">
        <span id="rsvp-adults" class="rsvp__stepper-label">Adults</span>
        <div class="rsvp__stepper-controls">
          <v-btn
            :disabled="adults === 0"
            class="rsvp__step"
            aria-label="One fewer adult"
            icon="mdi-minus"
            variant="outlined"
            @click="step('adults', -1)"
          />
          <output class="rsvp__count" aria-labelledby="rsvp-adults">{{ adults }}</output>
          <v-btn
            class="rsvp__step"
            aria-label="One more adult"
            icon="mdi-plus"
            variant="outlined"
            @click="step('adults', 1)"
          />
        </div>
      </div>

      <div class="rsvp__stepper">
        <span id="rsvp-children" class="rsvp__stepper-label">Kids</span>
        <div class="rsvp__stepper-controls">
          <v-btn
            :disabled="children === 0"
            class="rsvp__step"
            aria-label="One fewer child"
            icon="mdi-minus"
            variant="outlined"
            @click="step('children', -1)"
          />
          <output class="rsvp__count" aria-labelledby="rsvp-children">{{ children }}</output>
          <v-btn
            class="rsvp__step"
            aria-label="One more child"
            icon="mdi-plus"
            variant="outlined"
            @click="step('children', 1)"
          />
        </div>
      </div>

      <v-textarea
        v-model="note"
        label="Anything to add"
        placeholder="Running a bit late."
        rows="2"
        variant="outlined"
        density="comfortable"
        hide-details
      />

      <div class="rsvp__detail-actions">
        <v-btn
          :loading="saving"
          class="rsvp__action"
          color="primary"
          variant="flat"
          size="large"
          @click="saveDetail('Yes')"
        >
          We are coming
        </v-btn>
        <v-btn
          :disabled="saving"
          class="rsvp__action"
          variant="outlined"
          size="large"
          @click="saveDetail('Maybe')"
        >
          Maybe
        </v-btn>
        <v-btn
          :disabled="saving"
          class="rsvp__action"
          variant="outlined"
          size="large"
          @click="saveDetail('No')"
        >
          Can't make it
        </v-btn>
      </div>

      <v-btn variant="text" :disabled="saving" @click="editing = false">Back</v-btn>
    </div>
  </section>
</template>

<style scoped>
.rsvp {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.rsvp__announcement {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.rsvp__summary,
.rsvp__closed {
  margin: 0;
  color: rgb(var(--v-theme-on-surface-variant));
}

/*
  Every control here is at least 44 by 44, which is the smallest thing a thumb
  reliably hits (SPEC §9.5). The primary action is bigger still, because it is
  the one somebody is reaching for while holding a door open.
*/
.rsvp__primary {
  min-height: 3.5rem;
  font-size: 1.05rem;
}

.rsvp__secondary,
.rsvp__detail-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.rsvp__action {
  min-height: 2.75rem;
  min-width: 2.75rem;
  flex: 1 1 8rem;
}

.rsvp__detail {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.rsvp__stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.rsvp__stepper-label {
  font-size: 1.05rem;
}

.rsvp__stepper-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.rsvp__step {
  min-height: 2.75rem;
  min-width: 2.75rem;
}

.rsvp__count {
  font-size: 1.25rem;
  min-width: 2ch;
  text-align: center;
}
</style>
